import { ExportOutlined } from '@ant-design/icons'
import EyeOutlined from '@ant-design/icons/lib/icons/EyeOutlined'
import { Button, Card, Col, Row, Table, Tag, Tooltip } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import { UserMeyRealType, UserOnchainIdentity } from 'Modules'
import { useState } from 'react'

import LinkAddress from 'component/link/link-address'
import {
  USER_MEY_REAL_KYC_STATUS_MAP,
  USER_MEY_REAL_STATUS_MAP,
  USER_ONCHAIN_IDENTITY_STATUS_MAP,
  UserMeyRealKycStatusEnum,
  UserMeyRealStatusEnum,
} from 'constants/user-mey-real'
import { renderAddressUrl } from 'utils'

import ButtonBlockUser from './button-block-user'
import { ModalReferralPerformance } from './model-referral-performance'

interface UserMeyRealTableProps {
  data: UserMeyRealType[]
  loading: boolean
  pagination: TablePaginationConfig | false
  onChange?: (pagination: TablePaginationConfig) => void
  onRefresh?: () => void
}

const UserMeyRealTable = ({
  data,
  loading,
  pagination,
  onChange,
  onRefresh,
}: UserMeyRealTableProps) => {
  const [referralPerformanceAddress, setReferralPerformanceAddress] = useState<string | undefined>(
    undefined
  )

  const columns: ColumnsType<UserMeyRealType> = [
    {
      title: 'Wallet Address',
      dataIndex: 'walletAddress',
      key: 'walletAddress',
      width: 200,
      render: (text: string) => <LinkAddress address={text} type="address" />,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: 'Referral ID',
      dataIndex: 'refCode',
      key: 'refCode',
      width: 200,
      render(value, row) {
        if (!value) return null

        return (
          <Row gutter={8} align="middle">
            <Col>{value}</Col>
            <Col>
              <Button
                type="text"
                icon={<EyeOutlined />}
                onClick={() => setReferralPerformanceAddress(row.walletAddress)}
                size="small"
              />
            </Col>
          </Row>
        )
      },
    },
    {
      title: 'KYC Status',
      dataIndex: 'kycStatus',
      key: 'kycStatus',
      width: 130,
      align: 'center',
      render: (value: UserMeyRealKycStatusEnum) => {
        const statusInfo = USER_MEY_REAL_KYC_STATUS_MAP[value] || {
          label: 'Not Submitted',
          color: 'default',
        }
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
      },
    },
    {
      // ERC-3643 identity deployed after KYC - status tag + identity address + deploy tx
      title: 'Onchain Identity',
      dataIndex: 'onchainIdentity',
      key: 'onchainIdentity',
      width: 200,
      render: (identity?: UserOnchainIdentity | null) => {
        if (!identity) return <Tag>Not Created</Tag>
        const statusInfo = USER_ONCHAIN_IDENTITY_STATUS_MAP[identity.status] || {
          label: `Status ${identity.status}`,
          color: 'default',
        }
        const tag = <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
        return (
          <div>
            {identity.failReason ? <Tooltip title={identity.failReason}>{tag}</Tooltip> : tag}
            {identity.identityAddress ? (
              <div style={{ marginTop: 4 }}>
                <LinkAddress address={identity.identityAddress} />
                {identity.deployTxHash ? (
                  <Tooltip title="View deploy transaction">
                    <a
                      href={renderAddressUrl(identity.deployTxHash, 'tx')}
                      target="_blank"
                      rel="noreferrer"
                      style={{ marginLeft: 8 }}
                    >
                      <ExportOutlined />
                    </a>
                  </Tooltip>
                ) : null}
              </div>
            ) : null}
          </div>
        )
      },
    },
    {
      title: 'PTO Purchased',
      dataIndex: 'ptoPurchased',
      key: 'ptoPurchased',
      width: 130,
      align: 'center',
      render: (value: number) => value?.toLocaleString() || '0',
    },
    {
      title: 'Total Referred',
      dataIndex: 'totalReferrer',
      key: 'totalReferrer',
      width: 130,
      align: 'center',
    },
    {
      title: 'Total Earned',
      dataIndex: 'totalEarned',
      key: 'totalEarned',
      width: 130,
      align: 'center',
      render: (value: number) => (value ? `${value.toLocaleString()} MEY` : ''),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (value: UserMeyRealStatusEnum) => {
        const statusInfo = USER_MEY_REAL_STATUS_MAP[value] || {
          label: 'Unknown',
          color: 'default',
        }
        return <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      align: 'center',
      render: (_: unknown, record: UserMeyRealType) => (
        <ButtonBlockUser record={record} onSuccess={onRefresh} />
      ),
    },
  ]

  return (
    <Card>
      <Table<UserMeyRealType>
        columns={columns}
        dataSource={data}
        rowKey={(record) => record._id}
        loading={loading}
        pagination={pagination}
        onChange={onChange}
        scroll={{ x: 1100 }}
        bordered
      />

      {!!referralPerformanceAddress && (
        <ModalReferralPerformance
          walletAddress={referralPerformanceAddress}
          onClose={() => setReferralPerformanceAddress(undefined)}
        />
      )}
    </Card>
  )
}

export default UserMeyRealTable
